import EditableActions from './actions';
import {getFormFields, setFormFields, TableName, TableNames} from './options';
import _ from 'underscore';
import { EditableReduxHelper, findChild } from './helper';

let idx, items;

const initTableState = (formRef = { current: null}) => ({
    items: [],
    editLine: false,
    focusedElement: null,
    formRef,
    focusKey: null,
    isVoid: false,
    hasChildren: false,

    // Used to call "afterChange" callback
    // Currently only setItem, addItem, addChild, removeItem is supported
    refresh: 0,
    setFieldsValueCounter: 0,
    itemToUpdate: {},

    isAutofill: false, // in payment_deposit_items

    // local pagination ( for form_item table )
    pagination: {
        current: 1,
        pageSize: 100,
    },

    // For batch transactions to use list state
    context: {
        fields: {},
    },
});

function initState() {
    const initState = {
        hasActiveTable: false, // computed when there is an active table
        activeTable: null, // computed when there is an active table
        sortableWidths: null, // for sortables
    };
    TableNames.forEach(name => {
        initState[name] = initTableState();
    });
    return initState;
}

function generateKey() {
    return Math.random().toString(36);
}

function tableReducer(name, state, action) {
    switch (action.type) {
        case EditableActions.SET_FORM_REF:
            return {
                ...state,
                formRef: action.formRef,
            };

        case EditableActions.SET_PAGINATION:
            return {
                ...state,
                pagination: action.pagination,
            };

        case EditableActions.SET_VOID:
            return {
                ...state,
                isVoid: action.isVoid,
            };

        case EditableActions.SET_ITEMS: {
            let newItems = [...action.items];
            newItems = EditableReduxHelper.generateKey(newItems);

            // Replace null => undefined
            newItems = EditableReduxHelper.replaceNullWithUndefined(newItems);

            const isItemsChanged = !_.isEqual(state.items, newItems);

            return {
                ...state,
                items: isItemsChanged ? newItems : state.items,
                isVoid: action.isVoid === undefined ? state.isVoid : action.isVoid,
            };
        }
        case EditableActions.AFTER_SET_FIELDS_VALUE: {
            return {
                ...state,
                setFieldsValueCounter: state.setFieldsValueCounter + 1,
            };
        }
        case EditableActions.RESET_TABLE:
            return initTableState(state.formRef || {current: null});

        default:
            break;
    }
    if (state.isVoid)
        return state;

    // to use state in reducer
    const context = {
        fields: state.context.fields,
    };
    switch (action.type) {
        case EditableActions.SET_EDIT_LINE:
            return {
                ...state,
                editLine: action.key,
            };
        case EditableActions.CLEAR_EDIT_LINE:
            return {
                ...state,
                editLine: false,
            };

        case EditableActions.EDIT_PREV_LINE: {
            idx = state.items.findIndex(item => item.key === state.editLine); // current key.
            const key = (
                (idx !== -1 && state.items[idx - 1]) ?
                    state.items[idx - 1].key :
                    state.editLine
            );

            return {
                ...state,
                editLine: key,
            };
        }
        case EditableActions.EDIT_NEXT_LINE: {
            idx = state.items.findIndex(item => item.key === state.editLine); // current key.
            const key = (
                (idx !== -1 && state.items[idx + 1]) ?
                    state.items[idx + 1].key :
                    state.editLine
            );
            return {
                ...state,
                editLine: key,
            };
        }
        case EditableActions.SET_FOCUSED_ELEMENT:
            return {
                ...state,
                focusedElement: action.element,
            };
        case EditableActions.SET_FOCUS_KEY:
            return {
                ...state,
                focusKey: action.focusKey,
            };

        case EditableActions.SET_ITEM: // Support partial update (Must have key)
            return EditableReduxHelper.setItem(name, state, action, context);

        case EditableActions.ADD_ITEM: // Add to the end of the list (if parent
            return EditableReduxHelper.addItem(name, state, action, context);

        case EditableActions.REMOVE_ITEM: // Delete from the list
            return EditableReduxHelper.removeItem(name, state, action);

        case EditableActions.ADD_ITEMS: // Add to the end of the list; if key present, then update that key
            return EditableReduxHelper.addItems(name, state, action, context);

        // Add to the end of the list;
        // if key present, then update that key
        // NOT SUPPORTED: children / nested structure
        case EditableActions.SET_LINKED_ITEMS: {
            items = [...state.items];
            const newItems = action.items.map(item => {
                // No key => Create new key
                if (!item.key) {
                    return {
                        ...item,
                        key: generateKey(),
                    };
                }

                // Have key => Update item with key (Same as SET_ITEM)
                idx = items.findIndex(i => i.key === item.key);
                items[idx] = {
                    ...items[idx],
                    ...item,
                };
                return null;
            })
                .filter(item => !!item);

            // Remove
            const { keysToRemove } = action;
            const combinedItems = EditableReduxHelper.generateKey(
                [...items, ...newItems].filter(i => !keysToRemove.find(k => k === i.key)),
            );

            // Combine and Update line
            items = combinedItems.map(item => ({
                ...item,
                amount: undefined,
                discount_amount: undefined,
                tax_amount: undefined,
            }));

            return {
                ...state,
                items,
            };
        }
        case EditableActions.EDITABLE_SET_IS_AUTOFILL:
            return {
                ...state,
                isAutofill: action.value,
            };

        default:
            return state;
    }
}
function toForceUpdateForm(name, selectedItem, formRef) {
    const formData = getFormFields(name, formRef);

    // always update form if exchange_rate is changed (to reflect dp and dp2)
    if (formData?.exchange_rate !== selectedItem?.exchange_rate)
        return true;

    // for opening_stock_adjustment table
    if (formData?.product_id !== selectedItem?.product_id)
        return true;

    // For product bundle table discount row.
    // If sale/purchase_discount_amount is changed by BukkuNumberInputV2 (due to min or notEq), update the form to take in the updated values.
    if (formData?.sale_discount_amount &&
        parseFloat(formData?.sale_discount_amount) !== parseFloat(selectedItem?.sale_discount_amount))
        return true;
    if (formData?.purchase_discount_amount &&
        parseFloat(formData?.purchase_discount_amount) !== parseFloat(selectedItem?.purchase_discount_amount))
        return true;

    // BukkuNumberInput: (to handle invalid formula / number too large)
    // always update form if amount is changed while in autofill
    if (name === TableName.payment_deposit_item)
        return true;

    // console.log('aaa toForceUpdateForm', {
    //     name,
    //     selectedItem,
    //     formData,
    // });
    // always update form for batch due to multilines
    // without this, second line's invalid amount will persist in form after 2nd input onwards
    if ([
        TableName.batch_quotation, TableName.batch_sale_order,
        TableName.batch_invoice, TableName.batch_invoice_cash,
        TableName.batch_invoice_credit_note,
        TableName.batch_invoice_payment, TableName.batch_sale_refund,

        TableName.batch_purchase_order,
        TableName.batch_bill, TableName.batch_bill_cash, TableName.batch_bill_expense_claim,
        TableName.batch_bill_payment,

        TableName.batch_bank_income, TableName.batch_bank_expense,

        TableName.batch_journal_entry,
    ].includes(name))
        return true;

    return false;
}

function toUpdateForm(name, selectedItem, formRef) {
    const formData = getFormFields(name, formRef);

    const filteredItem = {};
    Object.keys(selectedItem || {}).forEach(key => {
        filteredItem[key] = formData?.[key];
    });

    if (toForceUpdateForm(name, selectedItem, formRef))
        return true;

    return !_.isEqual(filteredItem || selectedItem, formData);
}

export default function appReducer(state = initState(), action = {}) {
    // console.log('aaa appReducer Editable', {type: action.type, action, state});

    // All Table Redux
    switch (action.type) {
        case EditableActions.ClEAR_ALL_EDIT_LINE: {
            const newState = { ...state };
            TableNames.forEach(name => {
                newState[name].editLine = false;
            });
            return { ...newState, hasActiveTable: false, activeTable: null };
        }
        case EditableActions.SET_SORTABLE_WIDTHS:
            return { ...state, sortableWidths: action.widths };

        case EditableActions.SET_ACTIVE_TABLE:
            return {
                ...state,
                activeTable: action.name,
            };
        // Utils to get custom fields in reducer
        case EditableActions.ADD_BATCH_CUSTOM_FIELDS: {
            const newState = { ...state };
            newState[action.tableName].context = {
                fields: action.fields,
            };
            return { ...newState };
        }
        default:
            break;
    }

    // Table-specific Redux
    if (!action.name)
        return state;
    const table = state[action.name] || initTableState();
    const updatedTable = tableReducer(action.name, table, action);

    const {
        editLine: updatedTableEditLine,
        formRef: updatedTableFormRef,
        items: updatedTableItems,

        // shouldPreserveActiveTable is true when 'removeItem' from './helper.js' is called.
        // Refer to there for more a detailed comment.
        shouldPreserveActiveTable,
    } = updatedTable;

    // Update formRef (UI)
    if (updatedTableFormRef.current && !action.skipFormUpdate) {
        const editLineChanged = state[action.name].editLine !== updatedTableEditLine;
        const selectedItem = findChild(updatedTableItems, updatedTableEditLine);

        if (editLineChanged || toUpdateForm(action.name, selectedItem, updatedTableFormRef)) {
            const prevItemToUpdate = state[action.name].itemToUpdate;
            const nextItemToUpdate = setFormFields(action.name, selectedItem, table.context);

            if (!_.isEqual(prevItemToUpdate, nextItemToUpdate))
                updatedTable.itemToUpdate = nextItemToUpdate;

            updatedTable.forceUpdateForm = toForceUpdateForm(action.name, selectedItem, updatedTableFormRef);
        }
    }

    if (updatedTable.forceUpdateForm === true)
        console.log('aaa batch_invoice_payment - 5 Update formRef (UI', {updatedTable});

    return {
        ...state,
        [action.name]: {...updatedTable},
        hasActiveTable: state.hasActiveTable || !!(updatedTableEditLine || updatedTableEditLine === 0)
        || shouldPreserveActiveTable,
        activeTable: (updatedTableEditLine || shouldPreserveActiveTable) ? action.name : state.activeTable,
    };
}
